import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import s from './CopyField.module.css';

export type CopyFieldProps = {
  value: string;
  /** Accessible description of what's copied, e.g. "join code". */
  label: string;
  /** Big display-font value (join codes) or regular text (links). */
  variant?: 'code' | 'text';
  className?: string;
};

export function CopyField({ value, label, variant = 'code', className }: CopyFieldProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard unavailable: the value stays visible and selectable.
    }
  };

  return (
    <div className={cx(s.field, className)} data-variant={variant}>
      <output className={s.value} aria-label={label}>
        {value}
      </output>
      <button type="button" className={s.button} onClick={copy} aria-label={`Copy ${label}`}>
        <Icon icon={copied ? Check : Copy} size="md" />
        <span>{copied ? 'Copied!' : 'Copy'}</span>
      </button>
      <span className={s.sr} aria-live="polite">
        {copied ? `${label} copied` : ''}
      </span>
    </div>
  );
}
